import { createAsyncThunk } from '@reduxjs/toolkit';
import { LargeDataAnalyticsHostname } from "../configs/Domain";
import { createQueryParams } from './_apiUtils';

// 1) Map of endpoints
const UrlMap = {
  pcaDecomposition: `${LargeDataAnalyticsHostname}/analysis/decompose/pca`,
  ellipticalCopulas: `${LargeDataAnalyticsHostname}/dependence/copulas/elliptical`,
  simulatePortfolio: `${LargeDataAnalyticsHostname}/simulate/portfolio`,
};

// 2) Generate a unique key for each model fetch
export const generateModelKey = (modelName, args) => {
  const baseKey = `${modelName}_${args.symbols.join(',')}_${args.startDate || ''}_${args.endDate || ''}`;

  // For portfolioSimulation, add extra fields to the key
  if (modelName === 'portfolioSimulation') {
    const additionalParams = [
      args.nPaths || '1000',
      args.freq || 'm',
      args.initialCapital || '100000',
      args.nYears || '30'
    ].join('_');
    return `${baseKey}_${additionalParams}`;
  }

  return baseKey;
};

// 3) A factory to create a PCA or Copula thunk
export const createModelFetchThunk = (modelName, url) =>
  createAsyncThunk(
    `analytics/fetch${modelName}Model`,
    // Payload creator
    async ({ symbols, startDate, endDate }, { rejectWithValue }) => {
      const queryParams = createQueryParams({
        symbols,
        start_date: startDate,
        end_date: endDate,
      });
      try {
        const response = await fetch(`${url}?${queryParams}`);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        return { ...data, modelName };
      } catch (error) {
        return rejectWithValue(error.message);
      }
    },
    // 4) condition callback to skip fetching if key already exists
    {
      condition: ({ symbols, startDate, endDate }, { getState }) => {
        const key = generateModelKey(modelName, { symbols, startDate, endDate });
        const state = getState();
        // If we already have this model in the store, skip fetching
        return !state.analytics.models[key];
      },
    }
  );

// 5) A thunk specifically for Portfolio Simulations
export const fetchPortfolioSimulations = createAsyncThunk(
  'analytics/fetchPortfolioSimulations',
  async (
    // Destructure your needed arguments
    { symbols, nPaths = 1000, freq = 'm', initialCapital = 100000, nYears = 30 },
    // RTK gives us access to dispatch, getState, rejectWithValue, etc.
    { getState, rejectWithValue }
  ) => {
    // 1) Build the key
    const key = generateModelKey('portfolioSimulation', {
      symbols,
      nPaths,
      freq,
      initialCapital,
      nYears
    });

    // 2) Check if it’s already in Redux
    const state = getState();
    if (state.analytics?.portfolioSimulations?.[key]) {
      // We already have this data -> skip the fetch
      console.log("Skipping fetch for cached simulation:", key);

      // Return the existing data so the 'fulfilled' case still fires
      return {
        ...state.analytics.portfolioSimulations[key],
        // optionally attach these so the slice can use them again:
        symbols,
        nPaths,
        freq,
        initialCapital,
        nYears
      };
    }

    // 3) If not in store, build query & fetch
    const queryParams = createQueryParams({
      symbols,
      n_paths: nPaths,
      freq,
      initial_capital: initialCapital,
      n_years: nYears,
    });

    try {
      const response = await fetch(`${UrlMap.simulatePortfolio}?${queryParams}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();

      // Return result plus the arguments so the slice can build the same key again
      return {
        ...data,
        modelName: 'portfolioSimulation',
        symbols,
        nPaths,
        freq,
        initialCapital,
        nYears
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// 7) Export final thunks for usage in the slice
export const fetchPcaModel = createModelFetchThunk('pca', UrlMap.pcaDecomposition);
export const fetchCopulaModel = createModelFetchThunk('copula', UrlMap.ellipticalCopulas);
